package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t

internal data class EditorProposalActionClaim(
    val projectSlug: String,
    val locale: String,
    val keyId: String,
    val proposalId: String,
    val accept: Boolean,
) {
    val key = EditorProposalActionKey(projectSlug, locale, keyId, proposalId)
}

internal data class EditorProposalActionKey(
    val projectSlug: String,
    val locale: String,
    val keyId: String,
    val proposalId: String,
)

internal class EditorProposalActionRequest internal constructor(
    val claim: EditorProposalActionClaim,
    internal val requestId: Long,
    internal val statusPublicationId: Long,
)

/**
 * Owns proposal decision identity without coupling transport state to the editor's large domain
 * store. A request can publish only while its exact project/locale/key/proposal/action claim is
 * still current.
 */
@Stable
internal class EditorProposalActionStore(
    private val canWriteTranslations: () -> Boolean,
    private val assistStatuses: EditorAssistStatusStore = EditorAssistStatusStore(),
    private val transport: suspend (EditorProposalActionClaim) -> Unit,
) {
    val pending = mutableStateMapOf<EditorProposalActionKey, EditorProposalActionRequest>()
    private var nextRequestId = 1L

    fun begin(
        claim: EditorProposalActionClaim,
        currentClaim: () -> EditorProposalActionClaim?,
    ): EditorProposalActionRequest? {
        if (!canWriteTranslations() || currentClaim() != claim || pending[claim.key] != null) {
            return null
        }
        val status = assistStatuses.publishProposal(
            claim = claim,
            eventKind = EditorAssistStatusEventKind.PENDING,
            message = if (claim.accept) tr("editor.editor_proposal_action_store.accepting_agent_proposal", "Accepting agent proposal.") else tr("editor.editor_proposal_action_store.rejecting_agent_proposal", "Rejecting agent proposal."),
        )
        return EditorProposalActionRequest(
            claim = claim,
            requestId = nextRequestId++,
            statusPublicationId = status.publicationId,
        ).also { request ->
            pending[claim.key] = request
        }
    }

    suspend fun execute(
        request: EditorProposalActionRequest,
        currentClaim: () -> EditorProposalActionClaim?,
        onSuccess: (EditorProposalActionClaim) -> Unit,
        onFailure: (EditorProposalActionClaim, Throwable) -> Unit,
    ) {
        var terminalPublished = false
        try {
            if (!request.isCurrent(currentClaim)) return
            try {
                transport(request.claim)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (request.isCurrent(currentClaim)) {
                    val message = proposalFailureMessage(request.claim, failure)
                    assistStatuses.publishProposal(
                        claim = request.claim,
                        eventKind = EditorAssistStatusEventKind.FAILED,
                        message = message,
                        toastFeedback = EditorAssistToastFeedback(message, LToastVariant.DANGER),
                    )
                    terminalPublished = true
                    onFailure(request.claim, failure)
                }
                return
            }
            if (request.isCurrent(currentClaim)) {
                assistStatuses.publishProposal(
                    claim = request.claim,
                    eventKind = EditorAssistStatusEventKind.SUCCEEDED,
                    message = if (request.claim.accept) {
                        tr("editor.editor_proposal_action_store.agent_proposal_accepted", "Agent proposal accepted.")
                    } else {
                        tr("editor.editor_proposal_action_store.agent_proposal_rejected", "Agent proposal rejected.")
                    },
                    toastFeedback = if (request.claim.accept) {
                        EditorAssistToastFeedback("Accepted", LToastVariant.SUCCESS)
                    } else {
                        EditorAssistToastFeedback("Rejected", LToastVariant.NEUTRAL)
                    },
                )
                terminalPublished = true
                onSuccess(request.claim)
            }
        } catch (cancelled: CancellationException) {
            if (pending[request.claim.key] === request) {
                assistStatuses.publishProposal(
                    claim = request.claim,
                    eventKind = EditorAssistStatusEventKind.CANCELLED,
                    message = tr("editor.editor_proposal_action_store.proposal_action_was_cancelled", "Proposal action was cancelled."),
                )
                terminalPublished = true
            }
            throw cancelled
        } finally {
            if (pending[request.claim.key] === request) {
                if (!terminalPublished) {
                    assistStatuses.publishProposal(
                        claim = request.claim,
                        eventKind = EditorAssistStatusEventKind.CLEARED,
                        message = tr("editor.editor_proposal_action_store.proposal_action_stopped", "Proposal action stopped."),
                    )
                }
                pending.remove(request.claim.key)
            }
        }
    }

    fun isPending(claim: EditorProposalActionClaim): Boolean = pending[claim.key] != null

    private fun EditorProposalActionRequest.isCurrent(
        currentClaim: () -> EditorProposalActionClaim?,
    ): Boolean = canWriteTranslations() &&
        pending[claim.key] === this &&
        currentClaim() == claim
}

/**
 * Why an accept or a reject did not go through, as four whole sentences.
 *
 * It used to be assembled — `"Couldn't $action the agent proposal."` over an English verb, then
 * glued to either the server detail or a bare "Retry?". `EditorAssistAsyncStore` rejects exactly
 * that shape one file over, for the same reason: a sentence built from two fragments leaves a
 * translator a hole in the middle of it and no way to say how the halves join. One key per
 * (verb, has-detail) pair, and the detail is a named argument inside its own sentence.
 */
internal fun proposalFailureMessage(
    claim: EditorProposalActionClaim,
    failure: Throwable,
): String {
    val detail = failure.message?.takeIf(String::isNotBlank)
    return when {
        claim.accept && detail != null -> trf(
            "editor.editor_proposal_action_store.couldnt_accept_the_agent_proposal_detail",
            "Couldn't accept the agent proposal. {detail}",
            "detail" to detail,
        )

        claim.accept -> tr(
            "editor.editor_proposal_action_store.couldnt_accept_the_agent_proposal_retry",
            "Couldn't accept the agent proposal. Retry?",
        )

        detail != null -> trf(
            "editor.editor_proposal_action_store.couldnt_reject_the_agent_proposal_detail",
            "Couldn't reject the agent proposal. {detail}",
            "detail" to detail,
        )

        else -> tr(
            "editor.editor_proposal_action_store.couldnt_reject_the_agent_proposal_retry",
            "Couldn't reject the agent proposal. Retry?",
        )
    }
}
